// The web wallet: the demo's holder, attester and reviewer client (the Passport
// app comes later). Keys never leave the browser. A did:webvh is created with
// portable=true and posted to POST /did; a did:key identity needs no rail write.
import { ed25519 } from "@noble/curves/ed25519";
import { concat, fromHex, utf8 } from "../crypto/encoding";
import { signCompact, type Signer } from "../crypto/jws";
import { fromMultikey, toMultikey } from "../crypto/keys";
import { createEntry, nextEntry, verifyLog, type LogEntry } from "../crypto/webvh";
import { MAX_NAME, nameProblem, withDisplayName } from "../did/displayName";
import { rootSeedFromPhrase } from "./derive";
import { browserSigner, generateBrowserKey, rootSigner, type BrowserKey } from "./keys";
import type { KV } from "./store";

export type Method = "webvh" | "key";

export interface Delegation {
  id: string; issuer: string; scope: string[]; role: string; name?: string;
  validFrom?: string; validUntil?: string; credential: Record<string, any>;
}

export interface IdentityRecord {
  method: Method; did: string; rootMultikey: string; label?: string; rootKid?: string;
  /** This browser's key. `vmId` is its fragment in the DID (default "browser-1"). */
  browser?: { kind: BrowserKey["kind"]; multikey: string; vmId?: string };
  /** Set when this browser was linked from another device: it holds no #root. */
  linked?: boolean;
  /** Names for this DID's devices, kept in this browser only (a VM carries no custom properties). */
  deviceLabels?: Record<string, string>;
  delegations: Delegation[];
  log?: LogEntry[];
}

const PKCS8_ED25519 = fromHex("302e020100300506032b657004220420");
const b64uToBytes = (s: string) => Uint8Array.from(atob(s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4)), (c) => c.charCodeAt(0));

/** The multikey of the #root a phrase derives: what GET /dids/by-key looks up. */
export function rootMultikeyFromPhrase(phrase: string): string {
  return toMultikey({ kind: "Ed25519", raw: ed25519.getPublicKey(rootSeedFromPhrase(phrase)) });
}
const now = () => new Date().toISOString().replace(/\.\d{3}Z$/, "Z");

/** A device (browser key) listed in the DID's current document. */
export interface Device { vmId: string; multikey: string; kind: BrowserKey["kind"]; updateKey: boolean; thisBrowser: boolean; label?: string }

const DEVICE_RE = /^browser-(\d+)$/;
const fragment = (id: string) => id.slice(id.lastIndexOf("#") + 1);

export class Wallet {
  record?: IdentityRecord;
  private root?: Signer;
  private browser?: Signer;
  /** What the last fetch of this DID's delegations found (shown on /me). */
  delegationFetch?: { text: string; error?: boolean };

  constructor(private store: KV, private subtle: SubtleCrypto = globalThis.crypto.subtle) {}

  /** Restore what was saved in this browser. */
  async load(): Promise<IdentityRecord | undefined> {
    const rec = await this.store.get<IdentityRecord>("identity");
    if (!rec) return undefined;
    this.record = rec;
    const rootKey = await this.store.get<CryptoKey | Uint8Array>("root");
    if (rootKey) this.root = this.signerFromStoredRoot(rootKey, rec);
    const bk = await this.store.get<BrowserKey>("browser");
    if (bk && rec.browser) this.browser = browserSigner(bk, `${rec.did}#${rec.browser.vmId ?? "browser-1"}`, this.subtle);
    return rec;
  }

  /** Forget this browser's identity. */
  async clear(): Promise<void> {
    for (const k of ["identity", "root", "browser", "pending-link"]) await this.store.del(k);
    this.record = undefined; this.root = undefined; this.browser = undefined;
  }

  /**
   * Set up an identity from a recovery phrase. For did:webvh, `domain` is the
   * rail DID domain; the returned genesis entry must be posted to POST /did.
   * `create: false` restores an identity that already exists on the rail
   * (signing with #root; no new browser key is added without a DID update).
   */
  async fromPhrase(phrase: string, opts: { method: Method; domain?: string; create?: boolean;
    did?: string; label?: string; preferP256?: boolean }): Promise<{ record: IdentityRecord; genesis?: LogEntry }> {
    const seed = rootSeedFromPhrase(phrase);
    const rootMk = toMultikey({ kind: "Ed25519", raw: ed25519.getPublicKey(seed) });
    const didKey = `did:key:${rootMk}`;
    let record: IdentityRecord, genesis: LogEntry | undefined;
    if (opts.method === "key") {
      record = { method: "key", did: didKey, rootMultikey: rootMk, delegations: [], label: opts.label };
    } else if (opts.create === false) {
      if (!opts.did) throw new Error("restoring a did:webvh needs its DID");
      record = { method: "webvh", did: opts.did, rootMultikey: rootMk, delegations: [], label: opts.label };
    } else {
      if (!opts.domain) throw new Error("creating a did:webvh needs the rail DID domain");
      const bk = await generateBrowserKey(this.subtle, opts.preferP256);
      const bMk = toMultikey({ kind: bk.kind, raw: bk.raw });
      const id = `did:webvh:{SCID}:${opts.domain}:d:{SCID}`;
      const template = {
        "@context": ["https://www.w3.org/ns/did/v1", "https://w3id.org/security/multikey/v1"],
        id, alsoKnownAs: [didKey],
        verificationMethod: [
          { id: `${id}#root`, type: "Multikey", controller: id, publicKeyMultibase: rootMk },
          { id: `${id}#browser-1`, type: "Multikey", controller: id, publicKeyMultibase: bMk }],
        assertionMethod: [`${id}#root`, `${id}#browser-1`],
        authentication: [`${id}#root`, `${id}#browser-1`],
      };
      genesis = await createEntry(template, { updateKeys: [rootMk], versionTime: now(), signerMultikey: rootMk,
        sign: async (m) => ed25519.sign(m, seed), portable: true });
      record = { method: "webvh", did: genesis.state.id, rootMultikey: rootMk, delegations: [], label: opts.label,
                 browser: { kind: bk.kind, multikey: bMk }, log: [genesis] };
      await this.store.set("browser", bk);
      this.browser = browserSigner(bk, `${record.did}#browser-1`, this.subtle);
    }
    await this.store.set("root", await this.storableRoot(seed));
    await this.store.set("identity", record);
    this.record = record;
    this.root = rootSigner(seed, record.method === "key" ? `${didKey}#${rootMk}` : `${record.did}#root`);
    return { record, genesis };
  }

  /**
   * Import a synthetic identity from a key file (the `--keys-out` file of
   * `flask demo-setup`: {did, kid, private_jwk: {kty: "OKP", crv: "Ed25519", d}}).
   * For setting up demo actors whose keys were made outside this browser.
   */
  async fromKeyFile(entry: { did: string; kid: string; private_jwk: { kty: string; crv: string; d: string; x?: string } },
                    label?: string): Promise<IdentityRecord> {
    const jwk = entry.private_jwk;
    if (jwk?.kty !== "OKP" || jwk.crv !== "Ed25519" || !jwk.d) throw new Error("the key file must hold an Ed25519 private JWK");
    const seed = b64uToBytes(jwk.d);
    const rootMk = toMultikey({ kind: "Ed25519", raw: ed25519.getPublicKey(seed) });
    const method: Method = entry.did.startsWith("did:key:") ? "key" : "webvh";
    if (method === "key" && entry.did !== `did:key:${rootMk}`) throw new Error("the key does not match the did:key");
    const record: IdentityRecord = { method, did: entry.did, rootMultikey: rootMk, delegations: [], label, rootKid: entry.kid };
    await this.store.set("root", await this.storableRoot(seed));
    await this.store.set("identity", record);
    this.record = record; this.browser = undefined;
    this.root = rootSigner(seed, entry.kid);
    return record;
  }

  /**
   * A did:webvh update that sets (or, with null, removes) the self-asserted
   * display name, signed by an update key this browser holds (#root, or a linked Ed25519 device). `log` is the DID's
   * current log (GET /did/{did}/log); post the result to POST /did/{did}/keys.
   */
  async displayNameUpdate(log: LogEntry[], name: string | null, versionTime?: string): Promise<LogEntry> {
    if (!this.record || this.record.method !== "webvh") throw new Error("a display name needs a did:webvh; a did:key has no document to carry it");
    const clean = name === null ? null : name.trim().slice(0, MAX_NAME);
    if (clean !== null) { const why = nameProblem(clean); if (why) throw new Error(why); }
    const last = this.current(log);
    return this.update(last, withDisplayName(last.state, clean), undefined, versionTime);
  }

  // ---- devices ----------------------------------------------------------

  /**
   * Start linking this browser to an identity held on another device: make
   * this browser's key and keep it until the DID lists it. Show the returned
   * multikey as /me/link#add=<multikey>&label=… for the other device to open.
   */
  async startLink(opts: { preferP256?: boolean } = {}): Promise<{ kind: BrowserKey["kind"]; multikey: string }> {
    const pending = await this.pendingLink();
    if (pending && (!opts.preferP256 || pending.kind === "P-256")) return pending;
    const bk = await generateBrowserKey(this.subtle, opts.preferP256);
    await this.store.set("pending-link", bk);
    return { kind: bk.kind, multikey: toMultikey({ kind: bk.kind, raw: bk.raw }) };
  }

  /** The key a link in progress is waiting on, if any. */
  async pendingLink(): Promise<{ kind: BrowserKey["kind"]; multikey: string } | undefined> {
    const bk = await this.store.get<BrowserKey>("pending-link");
    return bk ? { kind: bk.kind, multikey: toMultikey({ kind: bk.kind, raw: bk.raw }) } : undefined;
  }

  /**
   * Finish a link: `log` is the DID's log, whose current document must list
   * this browser's pending key. The browser then signs as that device; it holds
   * no #root (an Ed25519 key is itself an update key, a P-256 key is not).
   */
  async finishLink(did: string, log: LogEntry[]): Promise<IdentityRecord> {
    const bk = await this.store.get<BrowserKey>("pending-link") ?? (this.record?.linked ? await this.store.get<BrowserKey>("browser") : undefined);
    if (!bk) throw new Error("no link is pending in this browser");
    const mk = toMultikey({ kind: bk.kind, raw: bk.raw });
    const last = lastOf(verifyLog(log, did));
    const vm = (last.state.verificationMethod ?? []).find((m: any) => m.publicKeyMultibase === mk && DEVICE_RE.test(fragment(m.id)));
    if (!vm) throw new Error("this browser's key is not listed in the DID's current document");
    const root = (last.state.verificationMethod ?? []).find((m: any) => fragment(m.id) === "root");
    const vmId = fragment(vm.id);
    const record: IdentityRecord = { method: "webvh", did, rootMultikey: root?.publicKeyMultibase ?? last.updateKeys[0],
      browser: { kind: bk.kind, multikey: mk, vmId }, linked: true, delegations: this.record?.did === did ? this.record.delegations : [] };
    await this.store.set("browser", bk);
    await this.store.del("pending-link");
    await this.store.del("root");
    await this.store.set("identity", record);
    this.record = record; this.root = undefined;
    this.browser = browserSigner(bk, `${did}#${vmId}`, this.subtle);
    return record;
  }

  /** The devices (browser keys) the DID's current document lists. */
  devices(log: LogEntry[]): Device[] {
    const last = this.current(log);
    return (last.state.verificationMethod ?? []).filter((m: any) => DEVICE_RE.test(fragment(m.id))).map((m: any) => {
      const vmId = fragment(m.id), mk = String(m.publicKeyMultibase);
      return { vmId, multikey: mk, kind: fromMultikey(mk).kind, updateKey: last.updateKeys.includes(mk),
        thisBrowser: this.record?.browser?.multikey === mk, label: this.record?.deviceLabels?.[vmId] };
    }).sort((a: Device, b: Device) => deviceNumber(a.vmId) - deviceNumber(b.vmId));
  }

  /** Whether this browser can sign an update of the DID (holds one of its update keys). */
  canUpdate(log?: LogEntry[]): boolean {
    if (!this.record || this.record.method !== "webvh") return false;
    const keys = log ? this.current(log).updateKeys : undefined;
    if (this.root && (!keys || keys.includes(this.record.rootMultikey))) return true;
    const b = this.record.browser;
    return !!(this.browser && b?.kind === "Ed25519" && (keys ? keys.includes(b.multikey) : this.record.linked));
  }

  /**
   * A did:webvh update adding a device: `#browser-n` (one past the highest
   * listed) in assertionMethod and authentication. An Ed25519 key also becomes
   * an update key (decided 2 Nov 2026); a P-256 key is assertion-only and never
   * an update key. Post the result to POST /did/{did}/keys.
   */
  async addDeviceUpdate(log: LogEntry[], multikey: string, versionTime?: string, label?: string): Promise<LogEntry> {
    const last = this.current(log);
    if (!this.canUpdate(log)) throw new Error(cannotUpdate);
    const kind = fromMultikey(multikey).kind;
    const st = last.state, did = st.id as string;
    const vms: any[] = st.verificationMethod ?? [];
    if (vms.some((m) => m.publicKeyMultibase === multikey)) throw new Error("that key is already in your DID");
    const n = Math.max(0, ...vms.map((m) => deviceNumber(fragment(m.id)))) + 1;
    const id = `${did}#browser-${n}`;
    const state = { ...st, verificationMethod: [...vms, { id, type: "Multikey", controller: did, publicKeyMultibase: multikey }],
      assertionMethod: [...(st.assertionMethod ?? []), id], authentication: [...(st.authentication ?? []), id] };
    const parameters = kind === "Ed25519" ? { updateKeys: [...last.updateKeys, multikey] } : undefined;
    const entry = await this.update(last, state, parameters, versionTime);
    if (label) await this.setDeviceLabel(`browser-${n}`, label);
    return entry;
  }

  /** A did:webvh update removing a device: its key, every reference to it, and its update key. */
  async removeDeviceUpdate(log: LogEntry[], vmId: string, versionTime?: string): Promise<LogEntry> {
    const last = this.current(log);
    if (vmId === "root") throw new Error("#root (from your phrase) cannot be removed; it is how you recover the identity");
    const st = last.state, did = st.id as string, id = `${did}#${vmId}`;
    const vm = (st.verificationMethod ?? []).find((m: any) => m.id === id && DEVICE_RE.test(vmId));
    if (!vm) throw new Error(`#${vmId} is not a device of this DID`);
    if (vm.publicKeyMultibase === this.record?.browser?.multikey)
      throw new Error("this browser cannot remove itself; remove it from another device, or forget the identity here");
    if (!this.canUpdate(log)) throw new Error(cannotUpdate);
    const drop = (xs: any[] | undefined) => xs?.filter((x) => (typeof x === "string" ? x : x?.id) !== id);
    const state: Record<string, any> = { ...st, verificationMethod: drop(st.verificationMethod) };
    for (const rel of ["assertionMethod", "authentication", "capabilityInvocation", "capabilityDelegation", "keyAgreement"])
      if (st[rel]) state[rel] = drop(st[rel]);
    const keys = last.updateKeys.filter((k) => k !== vm.publicKeyMultibase);
    if (!keys.length) throw new Error("that would leave the DID with no update key");
    const parameters = keys.length !== last.updateKeys.length ? { updateKeys: keys } : undefined;
    return this.update(last, state, parameters, versionTime);
  }

  async setDeviceLabel(vmId: string, label: string): Promise<void> {
    if (!this.record) return;
    this.record.deviceLabels = { ...this.record.deviceLabels, [vmId]: label.slice(0, 40) };
    await this.store.set("identity", this.record);
  }

  private current(log: LogEntry[]) {
    if (!this.record || this.record.method !== "webvh") throw new Error("only a did:webvh has a document to update");
    return lastOf(verifyLog(log, this.record.did));
  }

  /** Sign the next entry with an update key this browser holds: #root, else an Ed25519 device key. */
  private async update(last: ReturnType<typeof verifyLog>[number], state: Record<string, any>,
                       parameters: Record<string, any> | undefined, versionTime?: string): Promise<LogEntry> {
    const rec = this.record!;
    let signer: Signer | undefined, mk: string | undefined;
    if (this.root && last.updateKeys.includes(rec.rootMultikey)) { signer = this.root; mk = rec.rootMultikey; }
    else if (this.browser && rec.browser?.kind === "Ed25519" && last.updateKeys.includes(rec.browser.multikey)) {
      signer = this.browser; mk = rec.browser.multikey;
    }
    if (!signer || !mk) throw new Error(cannotUpdate);
    // versionTime is to the second and must be later than the previous entry's.
    const after = new Date(Date.parse(last.versionTime) + 1000).toISOString().replace(/\.\d{3}Z$/, "Z");
    const vt = versionTime ?? (Date.parse(now()) > Date.parse(last.versionTime) ? now() : after);
    const s = signer;
    return nextEntry(last.entry, state, { versionTime: vt, signerMultikey: mk, sign: (m) => s.sign(m), parameters });
  }

  get did(): string | undefined { return this.record?.did; }

  /** The signer for rail writes: the browser key when there is one, else #root. */
  signer(): Signer {
    const s = this.browser ?? this.root;
    if (!s) throw new Error("no identity in this browser");
    return s;
  }

  /** A rail write: compact JWS over {"kind", "client_ts", "entity"} (RailEnvelope). */
  async signEnvelope(kind: string, entity: Record<string, unknown>, clientTs = new Date().toISOString()): Promise<string> {
    const payload = utf8(JSON.stringify({ kind, client_ts: clientTs, entity }));
    return signCompact(this.signer(), payload);
  }

  /** Add an AuthorisedAttester credential issued to this identity. */
  async addDelegation(credential: Record<string, any>): Promise<Delegation> {
    if (!this.record) throw new Error("no identity in this browser");
    const types: string[] = credential?.type ?? [];
    const subject = credential?.credentialSubject ?? {};
    if (!types.includes("AuthorisedAttester")) throw new Error("this is not an AuthorisedAttester credential");
    if (subject.id !== this.record.did && subject.id !== `did:key:${this.record.rootMultikey}`)
      throw new Error(`this delegation was issued to ${subject.id}, not to this identity`);
    const d: Delegation = { id: String(credential.id), issuer: typeof credential.issuer === "string" ? credential.issuer : credential.issuer?.id,
      scope: subject.scope ?? [], role: subject.role ?? "", name: subject.name, validFrom: credential.validFrom,
      validUntil: credential.validUntil, credential };
    this.record.delegations = [...this.record.delegations.filter((x) => x.id !== d.id), d];
    await this.store.set("identity", this.record);
    return d;
  }

  /**
   * A request for the delegations issued to this DID (POST /dids/{did}/delegations):
   * a compact JWS by this browser's assertion key over a delegations_fetch envelope
   * with a fresh nonce (the rail accepts each JWS once).
   */
  async delegationsFetchJws(): Promise<string> {
    if (!this.record) throw new Error("no identity in this browser");
    const nonce = Array.from(globalThis.crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
    return this.signEnvelope("delegations_fetch", { holder_did: this.record.did, nonce });
  }

  /**
   * Take the delegations the rail says were issued to this DID: active ones are
   * added (each must name this identity as its subject); a revoked one is not
   * added, and is dropped if this browser held it.
   */
  async importDelegations(entries: { credential: Record<string, any>; status: "active" | "revoked"; scope?: string[] }[]):
      Promise<{ added: number; revoked: number; refused: number }> {
    if (!this.record) throw new Error("no identity in this browser");
    const mine = (c: any) => [this.record!.did, `did:key:${this.record!.rootMultikey}`].includes(c?.credentialSubject?.id);
    let added = 0, revoked = 0, refused = 0;
    for (const e of entries) {
      if (!mine(e.credential)) { refused++; continue; }
      const id = String(e.credential.id);
      if (e.status === "revoked") {
        revoked++;
        this.record.delegations = this.record.delegations.filter((d) => d.id !== id);
        continue;
      }
      const had = this.record.delegations.some((d) => d.id === id);
      try { await this.addDelegation(e.credential); if (!had) added++; } catch { refused++; }
    }
    await this.store.set("identity", this.record);
    return { added, revoked, refused };
  }

  /** The prepared artifact of one of this holder's claims, kept on this device only (for encrypting a copy per vouch link). */
  async saveArtifact(claimId: string, a: { bytes: Uint8Array; mime: string; hash: string }): Promise<void> {
    await this.store.set(`artifact:${claimId}`, a);
  }
  async loadArtifact(claimId: string): Promise<{ bytes: Uint8Array; mime: string; hash: string } | undefined> {
    return this.store.get(`artifact:${claimId}`);
  }

  /** Delegations that let this identity vouch for `issuer` on a claim of `claimType`, now. */
  delegationsFor(issuer: string, claimType: string, at = Date.now()): Delegation[] {
    return (this.record?.delegations ?? []).filter((d) => d.issuer === issuer && d.scope.includes(claimType)
      && (!d.validFrom || Date.parse(d.validFrom) <= at) && (!d.validUntil || at <= Date.parse(d.validUntil)));
  }

  private async storableRoot(seed: Uint8Array): Promise<CryptoKey | Uint8Array> {
    try {
      return await this.subtle.importKey("pkcs8", concat(PKCS8_ED25519, seed), { name: "Ed25519" }, false, ["sign"]);
    } catch { return seed; }   // no WebCrypto Ed25519: keep the seed in this browser's storage
  }

  private signerFromStoredRoot(k: CryptoKey | Uint8Array, rec: IdentityRecord): Signer {
    const kid = rec.rootKid ?? (rec.method === "key" ? `${rec.did}#${rec.rootMultikey}` : `${rec.did}#root`);
    if (k instanceof Uint8Array) return rootSigner(k, kid);
    const raw = rootRaw(rec.rootMultikey);
    return { alg: "EdDSA", kid, publicKey: { kind: "Ed25519", raw },
             sign: async (m) => new Uint8Array(await this.subtle.sign({ name: "Ed25519" }, k, m)) };
  }
}

const cannotUpdate = "this browser holds none of the DID's update keys, so it cannot change the identity; do this from a device that can (one with your phrase, or a linked Ed25519 device)";
const lastOf = <T>(xs: T[]): T => xs[xs.length - 1];
const deviceNumber = (vmId: string) => Number(DEVICE_RE.exec(vmId)?.[1] ?? 0);

function rootRaw(mk: string): Uint8Array {
  return fromMultikey(mk).raw;
}
